import { ColorInterval, ReportType, VideoSegment } from '../../reportsTypes';
import {
  AreaParams,
  AtonDetailedParams,
  AtonOverviewParams,
  AutoCompleteShipInfo,
  CoverageParams,
  CsvExportFastParams,
  CsvExportParams,
  DensityParams,
  FerryRouteParams,
  Filter,
  FishingParams,
  MmsiChangeParams,
  MultiShapeParams,
  NearMissShipToPointParams,
  NearMissShipToShipParams,
  PassagelineParams,
  PassagelineShipsParams,
  PilotParams,
  PortCallsParams,
  ReplayVideoParams,
  ReportBackendTypes,
  ShipsParams,
  SpeedOverGroundDistributionParams,
  SqlParams,
  TrackParams,
  TransitPilotParams,
  TransponderOffParams,
  TripsParams,
  WatchdogParams,
} from '@ais-ui/websockets';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { SelectType } from '@ais-ui/ui-core';
import {
  GeoJsonDictionary,
  PortDictionary,
  ReportFilterType,
  ShapeDictionaryType,
  translateFilterToBackend,
} from '../../utils';
import { getBackendColorScaleFromFrontend } from '../utils/densityColorMapping';
import { formatCustomDuration } from '../../../utils/formatDuration';
import { Ship } from '../utils/createDynamicFilterZodSchema';

function formatDate(date: Date) {
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const day = date.getUTCDate();
  const monthIndex = date.getUTCMonth();
  const year = date.getUTCFullYear();
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const seconds = date.getUTCSeconds();

  return `${day < 10 ? '0' + day : day} ${months[monthIndex]} ${year} ${
    hours < 10 ? '0' + hours : hours
  }:${minutes < 10 ? '0' + minutes : minutes}:${seconds < 10 ? '0' + seconds : seconds}`;
}

/**
 * Helper to build the MC (mail config) object for report params.
 * Uses the mailAction from miscellaneous if email(s) are provided.
 */
function buildMailConfig(miscellaneous: Record<string, any>): {
  who: string[];
  action: string;
} {
  // Handle both single email (string) and multiple emails (array)
  const emails = Array.isArray(miscellaneous.email)
    ? miscellaneous.email
    : miscellaneous.email
      ? [miscellaneous.email]
      : [];
  const hasEmail = emails.length > 0;
  return {
    who: emails,
    action: hasEmail ? miscellaneous.mailAction || 'notify' : '',
  };
}

const getAreaReportParams = (
  reportTemplateName: string,
  firstReportName: string,
  shape: GeoJson,
  startTime: Date,
  endTime: Date,
  miscellaneous: Record<string, any>,
  filter: Filter | string | null,
  reportTemplateId: string | undefined,
): AreaParams => {
  const { normalFilter, shiplistFilter } = translateFilterToBackend(filter);
  return {
    RDN: reportTemplateName,
    RDU: reportTemplateId ? reportTemplateId : null,
    NAME: firstReportName,
    GAC: {
      shape_id: shape.properties.id ? shape.properties.id : '',
      shape: JSON.stringify(shape),
    },
    SDAUSR: formatDate(startTime),
    EDAUSR: formatDate(endTime),
    STG: miscellaneous.statisticGrouping,
    SGA: 1,
    PI: miscellaneous.interval,
    GGD: 1,
    SHPL: shiplistFilter,
    FID: normalFilter?.uuid ? normalFilter.uuid : '',
    FN: normalFilter?.name ? normalFilter.name : '',
    FI: normalFilter?.filter ? normalFilter.filter : '',
    RSA: false,
    RS: {
      SI: null,
      SIS: -1,
      EDAUSR: '',
      MAXKEPT: 10,
      EXECTIME: null,
    },
    SR: miscellaneous.selectedGroups.map(
      (selectedGroup: SelectType) => selectedGroup.value,
    ),
    MC: buildMailConfig(miscellaneous),
    SUID: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
  };
};

const getMmsiChangeReportParams = (
  reportTemplateName: string,
  firstReportName: string,
  shape: GeoJson,
  startTime: Date,
  endTime: Date,
  miscellaneous: Record<string, any>,
  reportTemplateId: string | undefined,
): MmsiChangeParams => {
  return {
    RDN: reportTemplateName,
    RDU: reportTemplateId ? reportTemplateId : null,
    NAME: firstReportName,
    GAC: {
      shape_id: shape.properties.id ? shape.properties.id : '',
      shape: JSON.stringify(shape),
    },
    SDAUSR: formatDate(startTime),
    EDAUSR: formatDate(endTime),
    SGA: 1,
    GGD: 1,
    RSA: false,
    RS: {
      SI: null,
      SIS: -1,
      EDAUSR: '',
      MAXKEPT: 10,
      EXECTIME: null,
    },
    SR: miscellaneous.selectedGroups.map(
      (selectedGroup: SelectType) => selectedGroup.value,
    ),
    MC: buildMailConfig(miscellaneous),
    SUID: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
  };
};

const getShipsReportParams = (
  reportTemplateName: string,
  firstReportName: string,
  startTime: Date,
  endTime: Date,
  filter: AutoCompleteShipInfo[],
  miscellaneous: Record<string, any>,
  reportTemplateId: string | undefined,
): ShipsParams => {
  const { ships } = translateFilterToBackend(filter);

  return {
    RDN: reportTemplateName,
    RDU: reportTemplateId ? reportTemplateId : null,
    NAME: firstReportName,
    SDAUSR: formatDate(startTime),
    EDAUSR: formatDate(endTime),
    TL: ships,
    IGPS: 1,
    RSA: false,
    RS: {
      SI: null,
      SIS: -1,
      EDAUSR: '',
      MAXKEPT: 10,
      EXECTIME: null,
    },
    SR: miscellaneous.selectedGroups.map(
      (selectedGroup: SelectType) => selectedGroup.value,
    ),
    MC: buildMailConfig(miscellaneous),
    SUID: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
  };
};

const getTrackReportParams = (
  reportTemplateName: string,
  firstReportName: string,
  shape: GeoJson,
  startTime: Date,
  endTime: Date,
  filter: Filter | string | null,
  miscellaneous: Record<string, any>,
  reportTemplateId: string | undefined,
): TrackParams => {
  const { normalFilter, shiplistFilter } = translateFilterToBackend(filter);

  const colors = {
    colors: miscellaneous.colorByInterval.map(
      (colorInterval: ColorInterval) => ({
        val: null,
        rgb: colorInterval.color,
        sVal: colorInterval.startInterval,
        eVal: colorInterval.endInterval,
      }),
    ),
  };

  return {
    RDN: reportTemplateName,
    RDU: reportTemplateId ? reportTemplateId : null,
    NAME: firstReportName,
    GAC: {
      shape_id: shape.properties.id ? shape.properties.id : '',
      shape: JSON.stringify(shape),
    },
    SDAUSR: formatDate(startTime),
    EDAUSR: formatDate(endTime),
    CB: miscellaneous.colorBy,
    CBCD: JSON.stringify(colors),
    SHPL: shiplistFilter,
    FID: normalFilter?.uuid ? normalFilter.uuid : '',
    FN: normalFilter?.name ? normalFilter.name : '',
    FI: normalFilter?.filter ? normalFilter.filter : '',
    RSA: false,
    RS: {
      SI: null,
      SIS: -1,
      EDAUSR: '',
      MAXKEPT: 10,
      EXECTIME: null,
    },
    SR: miscellaneous.selectedGroups.map(
      (selectedGroup: SelectType) => selectedGroup.value,
    ),
    MC: buildMailConfig(miscellaneous),
    SUID: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
  };
};

const getDensityReportParams = (
  reportTemplateName: string,
  firstReportName: string,
  shape: GeoJson,
  startTime: Date,
  endTime: Date,
  filter: Filter | null | string,
  miscellaneous: Record<string, any>,
  reportTemplateId: string | undefined,
): DensityParams => {
  const { normalFilter, shiplistFilter } = translateFilterToBackend(filter);
  const colorScale = getBackendColorScaleFromFrontend(miscellaneous.colorScale);

  return {
    RDN: reportTemplateName,
    RDU: reportTemplateId ? reportTemplateId : null,
    NAME: firstReportName,
    GAC: {
      shape_id: shape.properties.id ? shape.properties.id : '',
      shape: JSON.stringify(shape),
    },
    SDAUSR: formatDate(startTime),
    EDAUSR: formatDate(endTime),
    SMML: 1,
    // SMMLMIN: 1, // Only set those if we choose that the user can do that otherwise they are set via reconfigure
    // SMMLMAX: 100, // Only set those if we choose that the user can do that otherwise they are set via reconfigure
    CS: colorScale,
    CB: miscellaneous.colorBy,
    CPD: parseInt(miscellaneous.cellsPerDegree, 10),
    CD: miscellaneous.colorDivision,
    SHPL: shiplistFilter,
    FID: normalFilter?.uuid ? normalFilter.uuid : '',
    FN: normalFilter?.name ? normalFilter.name : '',
    FI: normalFilter?.filter ? normalFilter.filter : '',
    RSA: false,
    RS: {
      SI: null,
      SIS: -1,
      EDAUSR: '',
      MAXKEPT: 10,
      EXECTIME: null,
    },
    SR: miscellaneous.selectedGroups.map(
      (selectedGroup: SelectType) => selectedGroup.value,
    ),
    MC: buildMailConfig(miscellaneous),
    SUID: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
  };
};

const getTripsReportParams = (
  reportTemplateName: string,
  firstReportName: string,
  arrivalShape: GeoJson,
  departureShape: GeoJson,
  startTime: Date,
  endTime: Date,
  maxDuration: string,
  excludeLocalOperations: boolean,
  filter: Filter | string | null,
  miscellaneous: Record<string, any>,
  reportTemplateId: string | undefined,
): TripsParams => {
  const { normalFilter, shiplistFilter } = translateFilterToBackend(filter);
  return {
    RDN: reportTemplateName,
    RDU: reportTemplateId ? reportTemplateId : null,
    NAME: firstReportName,
    DESTGAC: {
      GAC: {
        shape_id: arrivalShape.properties.id ? arrivalShape.properties.id : '',
        shape: JSON.stringify(arrivalShape),
      },
      WS: false,
    },
    SRCGAC: {
      GAC: {
        shape_id: departureShape.properties.id
          ? departureShape.properties.id
          : '',
        shape: JSON.stringify(departureShape),
      },
      WS: false,
    },
    SDAUSR: formatDate(startTime),
    EDAUSR: formatDate(endTime),
    MDUR: maxDuration,
    EL: excludeLocalOperations ? 1 : 0,
    FID: normalFilter?.uuid ? normalFilter.uuid : '',
    FN: normalFilter?.name ? normalFilter.name : '',
    FI: normalFilter?.filter ? normalFilter.filter : '',
    SHPL: shiplistFilter,
    RSA: false,
    RS: {
      SI: null,
      SIS: -1,
      EDAUSR: '',
      MAXKEPT: 10,
      EXECTIME: null,
    },
    SR: miscellaneous.selectedGroups.map(
      (selectedGroup: SelectType) => selectedGroup.value,
    ),
    MC: buildMailConfig(miscellaneous),
    SUID: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
  };
};

const getPassageLineReportParams = (
  reportTemplateName: string,
  firstReportName: string,
  shape: GeoJson,
  startTime: Date,
  endTime: Date,
  filter: Filter | string | null,
  miscellaneous: Record<string, any>,
  reportTemplateId: string | undefined,
): PassagelineParams => {
  const { normalFilter, shiplistFilter } = translateFilterToBackend(filter);

  let statisticGrouping = miscellaneous.statisticGrouping;

  if (!statisticGrouping && miscellaneous.outputType === 'histogram') {
    statisticGrouping = 'ungrouped';
  } else if (!statisticGrouping && miscellaneous.outputType === 'ships') {
    statisticGrouping = 'ungrouped';
  }

  return {
    RDN: reportTemplateName,
    RDU: reportTemplateId ? reportTemplateId : null,
    NAME: firstReportName,
    GAC: {
      shape_id: shape.properties.id ? shape.properties.id : '',
      shape: JSON.stringify(shape),
    },
    SDAUSR: formatDate(startTime),
    EDAUSR: formatDate(endTime),
    PAC: miscellaneous.outputType,
    STG: statisticGrouping,
    TYP: 'barplot',
    PI: miscellaneous.interval ? miscellaneous.interval : 'year',
    CAI: '',
    CC: miscellaneous.crossingConfig,
    MCI: miscellaneous.minCrossingInterval
      ? miscellaneous.minCrossingInterval + ':00'
      : '00:00:00',
    MCPAD: miscellaneous.minCrossingDistance
      ? miscellaneous.minCrossingDistance
      : 0,
    DCDV: miscellaneous.columnDivisionValue,
    DCDU: miscellaneous.columnDivisionUnit,
    SHPL: shiplistFilter,
    FID: normalFilter?.uuid ? normalFilter.uuid : '',
    FN: normalFilter?.name ? normalFilter.name : '',
    FI: normalFilter?.filter ? normalFilter.filter : '',
    RSA: false,
    RS: {
      SI: null,
      SIS: -1,
      EDAUSR: '',
      MAXKEPT: 10,
      EXECTIME: null,
    },
    SR: miscellaneous.selectedGroups.map(
      (selectedGroup: SelectType) => selectedGroup.value,
    ),
    MC: buildMailConfig(miscellaneous),
    SUID: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
  };
};

const getPassageLineShipsReportParams = (
  reportTemplateName: string,
  firstReportName: string,
  shape: ShapeObjectWithOrder,
  startTime: Date,
  endTime: Date,
  filter: Filter | string | null,
  miscellaneous: Record<string, any>,
  reportTemplateId: string | undefined,
): PassagelineShipsParams => {
  const { normalFilter, shiplistFilter } = translateFilterToBackend(filter);
  const maxDuration = formatCustomDuration(miscellaneous.maxDuration);
  return {
    RDN: reportTemplateName,
    RDU: reportTemplateId ? reportTemplateId : null,
    NAME: firstReportName,
    GAC: shape,
    SDAUSR: formatDate(startTime),
    EDAUSR: formatDate(endTime),
    PAC: miscellaneous.outputType,
    STG: miscellaneous.statisticGrouping ? miscellaneous.statisticGrouping : '',
    TYP: 'table',
    PI: miscellaneous.interval ? miscellaneous.interval : '',
    BDIR: miscellaneous.bothDirections ? 1 : 0,
    MDUR: maxDuration,
    SHPL: shiplistFilter,
    FID: normalFilter?.uuid ? normalFilter.uuid : '',
    FN: normalFilter?.name ? normalFilter.name : '',
    FI: normalFilter?.filter ? normalFilter.filter : '',
    RSA: false,
    RS: {
      SI: null,
      SIS: -1,
      EDAUSR: '',
      MAXKEPT: 10,
      EXECTIME: null,
    },
    SR: miscellaneous.selectedGroups.map(
      (selectedGroup: SelectType) => selectedGroup.value,
    ),
    MC: buildMailConfig(miscellaneous),
    SUID: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
  };
};

const getTransponderOffReportParams = (
  reportTemplateName: string,
  firstReportName: string,
  shape: GeoJson,
  startTime: Date,
  endTime: Date,
  filter: Filter | string | null,
  miscellaneous: Record<string, any>,
  reportTemplateId: string | undefined,
): TransponderOffParams => {
  const { normalFilter, shiplistFilter } = translateFilterToBackend(filter);
  const maxDuration = formatCustomDuration(miscellaneous.maxDuration);
  return {
    RDN: reportTemplateName,
    RDU: reportTemplateId ? reportTemplateId : null,
    NAME: firstReportName,
    GAC: {
      shape_id: shape.properties.id ? shape.properties.id : '',
      shape: JSON.stringify(shape),
    },
    SDAUSR: formatDate(startTime),
    EDAUSR: formatDate(endTime),
    MDUR: maxDuration,
    SHPL: shiplistFilter,
    FID: normalFilter?.uuid ? normalFilter.uuid : '',
    FN: normalFilter?.name ? normalFilter.name : '',
    FI: normalFilter?.filter ? normalFilter.filter : '',
    RSA: false,
    RS: {
      SI: null,
      SIS: -1,
      EDAUSR: '',
      MAXKEPT: 10,
      EXECTIME: null,
    },
    SR: miscellaneous.selectedGroups.map(
      (selectedGroup: SelectType) => selectedGroup.value,
    ),
    MC: buildMailConfig(miscellaneous),
    SUID: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
  };
};

const getFishingReportParams = (
  reportTemplateName: string,
  firstReportName: string,
  shape: GeoJson,
  startTime: Date,
  endTime: Date,
  filter: Filter | string | null,
  miscellaneous: Record<string, any>,
  reportTemplateId: string | undefined,
): FishingParams => {
  const minDuration = formatCustomDuration(miscellaneous.minDuration);
  const { normalFilter, shiplistFilter } = translateFilterToBackend(filter);
  return {
    RDN: reportTemplateName,
    RDU: reportTemplateId ? reportTemplateId : null,
    NAME: firstReportName,
    GAC: {
      shape_id: shape.properties.id ? shape.properties.id : '',
      shape: JSON.stringify(shape),
    },
    SDAUSR: formatDate(startTime),
    EDAUSR: formatDate(endTime),
    MIDUR: minDuration,
    SHPL: shiplistFilter,
    FID: normalFilter?.uuid ? normalFilter.uuid : '',
    FN: normalFilter?.name ? normalFilter.name : '',
    FI: normalFilter?.filter ? normalFilter.filter : '',
    RSA: false,
    RS: {
      SI: null,
      SIS: -1,
      EDAUSR: '',
      MAXKEPT: 10,
      EXECTIME: null,
    },
    SR: miscellaneous.selectedGroups.map(
      (selectedGroup: SelectType) => selectedGroup.value,
    ),
    MC: buildMailConfig(miscellaneous),
    SUID: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
  };
};

const getMultiShapeReportParams = (
  reportTemplateName: string,
  firstReportName: string,
  shape: ShapeObjectWithOrder,
  startTime: Date,
  endTime: Date,
  filter: Filter | null,
  miscellaneous: Record<string, any>,
  reportTemplateId: string | undefined,
): MultiShapeParams => {
  const { normalFilter, shiplistFilter } = translateFilterToBackend(filter);
  const maxDuration = formatCustomDuration(miscellaneous.maxDuration);
  return {
    RDN: reportTemplateName,
    RDU: reportTemplateId ? reportTemplateId : null,
    NAME: firstReportName,
    GAC: shape,
    SDAUSR: formatDate(startTime),
    EDAUSR: formatDate(endTime),
    PAC: miscellaneous.outputType,
    STG: miscellaneous.statisticGrouping ? miscellaneous.statisticGrouping : '',
    TYP: 'table',
    PI: miscellaneous.interval ? miscellaneous.interval : '',
    BDIR: miscellaneous.bothDirections ? 1 : 0,
    MDUR: maxDuration,
    SHPL: shiplistFilter,
    FID: normalFilter?.uuid ? normalFilter.uuid : '',
    FN: normalFilter?.name ? normalFilter.name : '',
    FI: normalFilter?.filter ? normalFilter.filter : '',
    RSA: false,
    RS: {
      SI: null,
      SIS: -1,
      EDAUSR: '',
      MAXKEPT: 10,
      EXECTIME: null,
    },
    SR: miscellaneous.selectedGroups.map(
      (selectedGroup: SelectType) => selectedGroup.value,
    ),
    MC: buildMailConfig(miscellaneous),
    SUID: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
  };
};

const getPilotReportParams = (
  reportTemplateName: string,
  firstReportName: string,
  shape: GeoJson,
  startTime: Date,
  endTime: Date,
  filter: Filter | null,
  miscellaneous: Record<string, any>,
  reportTemplateId: string | undefined,
): PilotParams => {
  const advancedParams = {
    md:
      miscellaneous.maxClosestPointOfApproachDistance !== undefined
        ? miscellaneous.maxClosestPointOfApproachDistance
        : null,
    min_dcog:
      miscellaneous.minDeltaCourseOverGround !== undefined
        ? miscellaneous.minDeltaCourseOverGround
        : null,
    max_dcog:
      miscellaneous.maxDeltaCourseOverGround !== undefined
        ? miscellaneous.maxDeltaCourseOverGround
        : null,
    min_dsog:
      miscellaneous.minDeltaSpeedOverGround !== undefined
        ? miscellaneous.minDeltaSpeedOverGround
        : null,
    max_dsog:
      miscellaneous.maxDeltaSpeedOverGround !== undefined
        ? miscellaneous.maxDeltaSpeedOverGround
        : null,
    min_sog:
      miscellaneous.minSpeedOverGround !== undefined
        ? miscellaneous.minSpeedOverGround
        : null,
    max_sog:
      miscellaneous.maxSpeedOverGround !== undefined
        ? miscellaneous.maxSpeedOverGround
        : null,
    min_dur:
      miscellaneous.minDuration !== undefined
        ? miscellaneous.minDuration
        : null,
    max_dur:
      miscellaneous.maxDuration !== undefined
        ? miscellaneous.maxDuration
        : null,
    min_dist:
      miscellaneous.minTravelDistance !== undefined
        ? miscellaneous.minTravelDistance
        : null,
    max_dist:
      miscellaneous.maxTravelDistance !== undefined
        ? miscellaneous.maxTravelDistance
        : null,
  };

  return {
    RDN: reportTemplateName,
    RDU: reportTemplateId ? reportTemplateId : null,
    NAME: firstReportName,
    GAC: {
      shape_id: shape.properties.id ? shape.properties.id : '',
      shape: JSON.stringify(shape),
    },
    SDAUSR: formatDate(startTime),
    EDAUSR: formatDate(endTime),
    SHPOP: JSON.stringify(advancedParams),
    SHPL: null,
    FID: filter?.uuid ? filter.uuid : '',
    FN: filter?.name ? filter.name : '',
    FI: filter?.filter ? filter.filter : '',
    RSA: false,
    RS: {
      SI: null,
      SIS: -1,
      EDAUSR: '',
      MAXKEPT: 10,
      EXECTIME: null,
    },
    SR: miscellaneous.selectedGroups.map(
      (selectedGroup: SelectType) => selectedGroup.value,
    ),
    MC: buildMailConfig(miscellaneous),
    SUID: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
  };
};

const getTransitPilotageReportParams = (
  reportTemplateName: string,
  firstReportName: string,
  shape: ShapeObjectWithOrder,
  startTime: Date,
  endTime: Date,
  maxDuration: string,
  shiplist: string,
  filter: Filter | null,
  miscellaneous: Record<string, any>,
  reportTemplateId: string | undefined,
): TransitPilotParams => {
  const advancedParams = {
    md:
      miscellaneous.maxClosestPointOfApproachDistance !== undefined
        ? miscellaneous.maxClosestPointOfApproachDistance
        : null,
    min_dcog:
      miscellaneous.minDeltaCourseOverGround !== undefined
        ? miscellaneous.minDeltaCourseOverGround
        : null,
    max_dcog:
      miscellaneous.maxDeltaCourseOverGround !== undefined
        ? miscellaneous.maxDeltaCourseOverGround
        : null,
    min_dsog:
      miscellaneous.minDeltaSpeedOverGround !== undefined
        ? miscellaneous.minDeltaSpeedOverGround
        : null,
    max_dsog:
      miscellaneous.maxDeltaSpeedOverGround !== undefined
        ? miscellaneous.maxDeltaSpeedOverGround
        : null,
    min_sog:
      miscellaneous.minSpeedOverGround !== undefined
        ? miscellaneous.minSpeedOverGround
        : null,
    max_sog:
      miscellaneous.maxSpeedOverGround !== undefined
        ? miscellaneous.maxSpeedOverGround
        : null,
    min_dur:
      miscellaneous.minDuration !== undefined
        ? miscellaneous.minDuration
        : null,
    max_dur:
      miscellaneous.maxDuration !== undefined
        ? miscellaneous.maxDuration
        : null,
    min_dist:
      miscellaneous.minTravelDistance !== undefined
        ? miscellaneous.minTravelDistance
        : null,
    max_dist:
      miscellaneous.maxTravelDistance !== undefined
        ? miscellaneous.maxTravelDistance
        : null,
  };

  return {
    RDN: reportTemplateName,
    RDU: reportTemplateId ? reportTemplateId : null,
    NAME: firstReportName,
    GAC: shape,
    SDAUSR: formatDate(startTime),
    EDAUSR: formatDate(endTime),
    SHPOP: JSON.stringify(advancedParams),
    SHPL: shiplist,
    FID: filter?.uuid ? filter.uuid : '',
    FN: filter?.name ? filter.name : '',
    FI: filter?.filter ? filter.filter : '',
    RSA: false,
    RS: {
      SI: null,
      SIS: -1,
      EDAUSR: '',
      MAXKEPT: 10,
      EXECTIME: null,
    },
    SR: miscellaneous.selectedGroups.map(
      (selectedGroup: SelectType) => selectedGroup.value,
    ),
    MC: buildMailConfig(miscellaneous),
    SUID: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
    MDUR: maxDuration,
  };
};

const getSpeedOverGroundReportParams = (
  reportTemplateName: string,
  firstReportName: string,
  shape: GeoJson,
  startTime: Date,
  endTime: Date,
  filter: AutoCompleteShipInfo[] | null | string,
  miscellaneous: Record<string, any>,
  reportTemplateId: string | undefined,
): SpeedOverGroundDistributionParams => {
  const { ships, shiplistFilter } = translateFilterToBackend(filter);

  return {
    RDN: reportTemplateName,
    RDU: reportTemplateId ? reportTemplateId : null,
    NAME: firstReportName,
    WS: 0,
    GAC: {
      shape_id: shape.properties.id ? shape.properties.id : '',
      shape: JSON.stringify(shape),
    },
    SDAUSR: formatDate(startTime),
    EDAUSR: formatDate(endTime),
    TL: ships,
    SHPL: shiplistFilter,
    STG: miscellaneous.distribution,
    PI: miscellaneous.interval,
    EXMSG27: 0,
    RSA: false,
    RS: {
      SI: null,
      SIS: -1,
      EDAUSR: '',
      MAXKEPT: 10,
      EXECTIME: null,
    },
    SR: miscellaneous.selectedGroups.map(
      (selectedGroup: SelectType) => selectedGroup.value,
    ),
    MC: buildMailConfig(miscellaneous),
    SUID: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
  };
};

const getPortCallsReportParams = (
  reportTemplateName: string,
  firstReportName: string,
  shape: GeoJson | null,
  portIds: string[],
  worldShape: boolean,
  startTime: Date,
  endTime: Date,
  filter: Filter | string | null,
  miscellaneous: Record<string, any>,
  reportTemplateId: string | undefined,
): PortCallsParams => {
  const { normalFilter, shiplistFilter } = translateFilterToBackend(filter);

  let backendShape = null;
  if (shape) {
    backendShape = {
      shape_id: shape.properties.id ? shape.properties.id : '',
      shape: JSON.stringify(shape),
    };
  }

  const backendPorts: number[] = portIds.map((portId) => parseInt(portId));

  return {
    RDN: reportTemplateName,
    RDU: reportTemplateId ? reportTemplateId : null,
    NAME: firstReportName,
    WS: worldShape ? 1 : 0,
    GAC: backendShape,
    PORT: backendPorts,
    SDAUSR: formatDate(startTime),
    EDAUSR: formatDate(endTime),
    SHPL: shiplistFilter,
    FID: normalFilter?.uuid ? normalFilter.uuid : '',
    FN: normalFilter?.name ? normalFilter.name : '',
    FI: normalFilter?.filter ? normalFilter.filter : '',
    RSA: false,
    RS: {
      SI: null,
      SIS: -1,
      EDAUSR: '',
      MAXKEPT: 10,
      EXECTIME: null,
    },
    SR: miscellaneous.selectedGroups.map(
      (selectedGroup: SelectType) => selectedGroup.value,
    ),
    MC: buildMailConfig(miscellaneous),
    SUID: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
  };
};

const getFerryRouteReportParams = (
  reportTemplateName: string,
  firstReportName: string,
  startTime: Date,
  endTime: Date,
  filter: string | null,
  miscellaneous: Record<string, any>,
  reportTemplateId: string | undefined,
): FerryRouteParams => {
  return {
    RDN: reportTemplateName,
    RDU: reportTemplateId ? reportTemplateId : null,
    NAME: firstReportName,
    SDAUSR: formatDate(startTime),
    EDAUSR: formatDate(endTime),
    SHPL: filter,
    FID: '',
    FN: '',
    FI: '',
    RSA: false,
    RS: {
      SI: null,
      SIS: -1,
      EDAUSR: '',
      MAXKEPT: 10,
      EXECTIME: null,
    },
    SR: miscellaneous.selectedGroups.map(
      (selectedGroup: SelectType) => selectedGroup.value,
    ),
    MC: buildMailConfig(miscellaneous),
    SUID: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
  };
};

const getCoverageReportParams = (
  reportTemplateName: string,
  firstReportName: string,
  shape: GeoJson,
  startTime: Date,
  endTime: Date,
  filter: Filter | null,
  miscellaneous: Record<string, any>,
  reportTemplateId: string | undefined,
): CoverageParams => {
  return {
    RDN: reportTemplateName,
    RDU: reportTemplateId ? reportTemplateId : null,
    NAME: firstReportName,
    GAC: {
      shape_id: shape.properties.id ? shape.properties.id : '',
      shape: JSON.stringify(shape),
    },
    SDAUSR: formatDate(startTime),
    EDAUSR: formatDate(endTime),
    CCR: 1,
    CR: miscellaneous.cellResolution,
    CD: miscellaneous.colorDivision,
    CPTYP: miscellaneous.plotType,
    FID: filter?.uuid ? filter.uuid : '',
    FN: filter?.name ? filter.name : '',
    FI: filter?.filter ? filter.filter : '',
    RSA: false,
    RS: {
      SI: null,
      SIS: -1,
      EDAUSR: '',
      MAXKEPT: 10,
      EXECTIME: null,
    },
    SR: miscellaneous.selectedGroups.map(
      (selectedGroup: SelectType) => selectedGroup.value,
    ),
    MC: buildMailConfig(miscellaneous),
    SUID: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
  };
};

const getNearMissShipToPointReportParams = (
  reportTemplateName: string,
  firstReportName: string,
  shape: GeoJson,
  startTime: Date,
  endTime: Date,
  filter: Filter | null,
  miscellaneous: Record<string, any>,
  reportTemplateId: string | undefined,
): NearMissShipToPointParams => {
  const { normalFilter, shiplistFilter } = translateFilterToBackend(filter);

  let LAT, LON;
  if (shape.geometry && shape.geometry.type === 'Point') {
    LON = shape.geometry.coordinates[0];
    LAT = shape.geometry.coordinates[1];
  } else {
    console.warn('Near miss ship to point report did not receive a point');
    LON = 0;
    LAT = 0;
  }

  return {
    RDN: reportTemplateName,
    RDU: reportTemplateId ? reportTemplateId : null,
    NAME: firstReportName,
    LAT: LAT,
    LON: LON,
    SDAUSR: formatDate(startTime),
    EDAUSR: formatDate(endTime),
    DT: miscellaneous.distanceThreshold,
    SHPL: shiplistFilter,
    FID: normalFilter?.uuid ? normalFilter.uuid : '',
    FN: normalFilter?.name ? normalFilter.name : '',
    FI: normalFilter?.filter ? normalFilter.filter : '',
    RSA: false,
    RS: {
      SI: null,
      SIS: -1,
      EDAUSR: '',
      MAXKEPT: 10,
      EXECTIME: null,
    },
    SR: miscellaneous.selectedGroups.map(
      (selectedGroup: SelectType) => selectedGroup.value,
    ),
    MC: buildMailConfig(miscellaneous),
    SUID: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
  };
};

const getNearMissShipToShipReportParams = (
  reportTemplateName: string,
  firstReportName: string,
  shape: GeoJson,
  startTime: Date,
  endTime: Date,
  filter: Filter | AutoCompleteShipInfo[] | string | null,
  miscellaneous: Record<string, any>,
  reportTemplateId: string | undefined,
): NearMissShipToShipParams => {
  const { ships, normalFilter, shiplistFilter } =
    translateFilterToBackend(filter);

  const advancedParams = {
    pid: 'cd',
    min_dcog:
      miscellaneous.minDeltaCourseOverGround !== undefined
        ? miscellaneous.minDeltaCourseOverGround
        : null,
    max_dcog:
      miscellaneous.maxDeltaCourseOverGround !== undefined
        ? miscellaneous.maxDeltaCourseOverGround
        : null,
    min_dsog:
      miscellaneous.minDeltaSpeedOverGround !== undefined
        ? miscellaneous.minDeltaSpeedOverGround
        : null,
    max_dsog:
      miscellaneous.maxDeltaSpeedOverGround !== undefined
        ? miscellaneous.maxDeltaSpeedOverGround
        : null,
    min_sog:
      miscellaneous.minSpeedOverGround !== undefined
        ? miscellaneous.minSpeedOverGround
        : null,
    max_sog:
      miscellaneous.maxSpeedOverGround !== undefined
        ? miscellaneous.maxSpeedOverGround
        : null,
    min_dur:
      miscellaneous.minDuration !== undefined
        ? miscellaneous.minDuration
        : null,
    max_dur:
      miscellaneous.maxDuration !== undefined
        ? miscellaneous.maxDuration
        : null,
    min_dist:
      miscellaneous.minTravelDistance !== undefined
        ? miscellaneous.minTravelDistance
        : null,
    max_dist:
      miscellaneous.maxTravelDistance !== undefined
        ? miscellaneous.maxTravelDistance
        : null,
  };

  return {
    RDN: reportTemplateName,
    RDU: reportTemplateId ? reportTemplateId : null,
    NAME: firstReportName,
    GAC: {
      shape_id: shape.properties.id ? shape.properties.id : '',
      shape: JSON.stringify(shape),
    },
    SDAUSR: formatDate(startTime),
    EDAUSR: formatDate(endTime),
    MCD: miscellaneous.maxClosestPointOfApproachDistance,
    NMSSAS: JSON.stringify(advancedParams),
    TL: ships,
    SHPL: shiplistFilter,
    FID: normalFilter?.uuid ? normalFilter.uuid : '',
    FN: normalFilter?.name ? normalFilter.name : '',
    FI: normalFilter?.filter ? normalFilter.filter : '',
    RSA: false,
    RS: {
      SI: null,
      SIS: -1,
      EDAUSR: '',
      MAXKEPT: 10,
      EXECTIME: null,
    },
    SR: miscellaneous.selectedGroups.map(
      (selectedGroup: SelectType) => selectedGroup.value,
    ),
    MC: buildMailConfig(miscellaneous),
    SUID: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
  };
};

const getCsvExportReportParams = (
  reportTemplateName: string,
  firstReportName: string,
  shape: GeoJson,
  startTime: Date,
  endTime: Date,
  filter: Filter | null,
  miscellaneous: Record<string, any>,
  reportTemplateId: string | undefined,
): CsvExportParams => {
  return {
    RDN: reportTemplateName,
    RDU: reportTemplateId ? reportTemplateId : null,
    NAME: firstReportName,
    GAC: {
      shape_id: shape.properties.id ? shape.properties.id : '',
      shape: JSON.stringify(shape),
    },
    SDAUSR: formatDate(startTime),
    EDAUSR: formatDate(endTime),
    RSD: 'raw',
    CSVP: miscellaneous.dataFields.map(
      (dataField: SelectType) => dataField.value,
    ),
    MSGT: miscellaneous.messageType.map(
      (dataField: SelectType) => dataField.value,
    ),
    MF: miscellaneous.fewFiles ? 1 : 0,
    SRATE: null,
    SHPL: null,
    FID: filter?.uuid ? filter.uuid : '',
    FN: filter?.name ? filter.name : '',
    FI: filter?.filter ? filter.filter : '',
    RSA: false,
    RS: {
      SI: null,
      SIS: -1,
      EDAUSR: '',
      MAXKEPT: 10,
      EXECTIME: null,
    },
    SR: miscellaneous.selectedGroups.map(
      (selectedGroup: SelectType) => selectedGroup.value,
    ),
    MC: buildMailConfig(miscellaneous),
    SUID: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
  };
};

const getCsvExportFastReportParams = (
  reportTemplateName: string,
  firstReportName: string,
  shape: GeoJson,
  startTime: Date,
  endTime: Date,
  filter: Filter | null,
  miscellaneous: Record<string, any>,
  reportTemplateId: string | undefined,
): CsvExportFastParams => {
  return {
    RDN: reportTemplateName,
    RDU: reportTemplateId ? reportTemplateId : null,
    NAME: firstReportName,
    GAC: {
      shape_id: shape.properties.id ? shape.properties.id : '',
      shape: JSON.stringify(shape),
    },
    SDAUSR: formatDate(startTime),
    EDAUSR: formatDate(endTime),
    CSVPTB: miscellaneous.dataFields.map(
      (dataField: SelectType) => dataField.value,
    ),
    MF: miscellaneous.fewFiles ? 1 : 0,
    SRATE: null,
    SHPL: null,
    FID: filter?.uuid ? filter.uuid : '',
    FN: filter?.name ? filter.name : '',
    FI: filter?.filter ? filter.filter : '',
    RSA: false,
    RS: {
      SI: null,
      SIS: -1,
      EDAUSR: '',
      MAXKEPT: 10,
      EXECTIME: null,
    },
    SR: miscellaneous.selectedGroups.map(
      (selectedGroup: SelectType) => selectedGroup.value,
    ),
    MC: buildMailConfig(miscellaneous),
    SUID: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
  };
};

const getWatchdogReportParams = (
  reportTemplateName: string,
  firstReportName: string,
  shape: GeoJson,
  startTime: Date,
  endTime: Date,
  miscellaneous: Record<string, any>,
  reportTemplateId: string | undefined,
): WatchdogParams => {
  return {
    RDN: reportTemplateName,
    RDU: reportTemplateId ? reportTemplateId : null,
    NAME: firstReportName,
    GAC: {
      shape_id: shape.properties.id ? shape.properties.id : '',
      shape: JSON.stringify(shape),
    },
    SDAUSR: formatDate(startTime),
    EDAUSR: formatDate(endTime),
    WD: miscellaneous.watchdogDefinitions.map(
      (dataField: SelectType) => dataField.value,
    ),
    RSA: false,
    RS: {
      SI: null,
      SIS: -1,
      EDAUSR: '',
      MAXKEPT: 10,
      EXECTIME: null,
    },
    SR: miscellaneous.selectedGroups.map(
      (selectedGroup: SelectType) => selectedGroup.value,
    ),
    MC: buildMailConfig(miscellaneous),
    SUID: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
  };
};

const getReplayVideoReportParams = (
  reportTemplateName: string,
  firstReportName: string,
  shape: GeoJson,
  startTime: Date,
  endTime: Date,
  miscellaneous: Record<string, any>,
  reportTemplateId: string | undefined,
  ships: Ship[] | undefined | null,
  shipAttribute: 'colorBy' | 'filterBy' | undefined,
): ReplayVideoParams => {
  const mappedShips = ships?.map((ship) => ({
    name: ship.name ?? null,
    mmsi: ship.mmsi ?? null,
    callsign: ship.callSign ?? null,
    imo: ship.imo ?? null,
  }));

  const [width, height] = miscellaneous.videoResolution.split('x').map(Number);

  const videoResolution = {
    W: width,
    H: height,
  };

  const videoSegments = miscellaneous.videoSegments.map(
    (segment: VideoSegment) => ({
      start: segment?.dateRange?.from?.getTime(),
      startUsr: segment?.dateRange?.from
        ? formatDate(segment?.dateRange?.from)
        : undefined,
      end: segment?.dateRange?.to?.getTime(),
      endUsr: segment?.dateRange?.to
        ? formatDate(segment?.dateRange?.to)
        : undefined,
      shapeUuid: segment.shapeId,
      shape: segment?.shape?.properties?.name,
      speed: segment.videoSpeed,
      showLabels: segment.showLabels,
      showAllShips: segment.showAllShips,
    }),
  );

  return {
    RDN: reportTemplateName,
    RDU: reportTemplateId ? reportTemplateId : null,
    NAME: firstReportName,
    SDAUSR: formatDate(startTime),
    EDAUSR: formatDate(endTime),
    SHPL: null,
    GAC: {
      shape_id: shape.properties.id ? shape.properties.id : '',
      shape: JSON.stringify(shape),
    },
    FID: '',
    FN: '',
    FI: '',
    TL: mappedShips ? mappedShips : [],
    VT: miscellaneous.videoTitle ?? '',
    VS: parseInt(miscellaneous.videoSpeed),
    VR: videoResolution,
    VF: miscellaneous.videoFormat,
    VQ: miscellaneous.videoQuality,
    MAT: miscellaneous.mapType,
    SAS: shipAttribute === 'colorBy' ? 1 : 0, // 1 for colorBy, 0 for filterBy
    ST: miscellaneous.showTracks ?? false,
    TLT: 24,
    VC: videoSegments.length > 0 ? 1 : 0, // 1 if there are video segments, 0 otherwise
    VCE: videoSegments,
    SR: miscellaneous.selectedGroups.map(
      (selectedGroup: SelectType) => selectedGroup.value,
    ),
    RS: null,
    MC: buildMailConfig(miscellaneous),
    SUID: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
  };
};

const getSqlReportParams = (
  reportTemplateName: string,
  firstReportName: string,
  shape: GeoJson,
  startTime: Date,
  endTime: Date,
  miscellaneous: Record<string, any>,
  reportTemplateId: string | undefined,
): SqlParams => {
  return {
    RDN: reportTemplateName,
    RDU: reportTemplateId ? reportTemplateId : null,
    NAME: firstReportName,
    SDAUSR: formatDate(startTime),
    EDAUSR: formatDate(endTime),
    GAC: {
      shape_id: shape.properties.id ? shape.properties.id : '',
      shape: JSON.stringify(shape),
    },
    SQL: miscellaneous.query,
    ST: false,
    SR: miscellaneous.selectedGroups.map(
      (selectedGroup: SelectType) => selectedGroup.value,
    ),
    RS: null,
    MC: buildMailConfig(miscellaneous),
    SUID: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
  };
};

const getAtonDetailedReportParams = (
  reportTemplateName: string,
  firstReportName: string,
  startTime: Date,
  endTime: Date,
  miscellaneous: Record<string, any>,
  reportTemplateId: string | undefined,
): AtonDetailedParams => {
  return {
    RDN: reportTemplateName,
    RDU: reportTemplateId ? reportTemplateId : null,
    NAME: firstReportName,
    SDAUSR: formatDate(startTime),
    EDAUSR: formatDate(endTime),
    MMSI: miscellaneous.mmsi,
    RSA: false,
    RS: {
      SI: null,
      SIS: -1,
      EDAUSR: '',
      MAXKEPT: 10,
      EXECTIME: null,
    },
    SR: miscellaneous.selectedGroups.map(
      (selectedGroup: SelectType) => selectedGroup.value,
    ),
    MC: buildMailConfig(miscellaneous),
    SUID: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
  };
};

const getAtonOverviewReportParams = (
  reportTemplateName: string,
  firstReportName: string,
  startTime: Date,
  endTime: Date,
  miscellaneous: Record<string, any>,
  reportTemplateId: string | undefined,
): AtonOverviewParams => {
  return {
    RDN: reportTemplateName,
    RDU: reportTemplateId ? reportTemplateId : null,
    NAME: firstReportName,
    SDAUSR: formatDate(startTime),
    EDAUSR: formatDate(endTime),
    RSA: false,
    RS: {
      SI: null,
      SIS: -1,
      EDAUSR: '',
      MAXKEPT: 10,
      EXECTIME: null,
    },
    SR: miscellaneous.selectedGroups.map(
      (selectedGroup: SelectType) => selectedGroup.value,
    ),
    MC: buildMailConfig(miscellaneous),
    SUID: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
  };
};

const getSingleShape = (
  reportType: ReportType,
  shapeDictionary: GeoJsonDictionary | null,
  _miscellaneous?: Record<string, any>,
): GeoJson | null => {
  if (!shapeDictionary) {
    return null;
  }
  if (
    !reportType.shape ||
    !reportType.shape.shape ||
    reportType.shape.shape.length === 0
  ) {
    return null;
  }

  const shapeName = reportType.shape.shape[0].name;
  const shape = shapeDictionary[shapeName];
  return shape;
};

const getPortIds = (
  reportType: ReportType,
  shapeDictionary: PortDictionary | null,
): string[] => {
  if (!shapeDictionary) {
    return [];
  }
  if (
    !reportType.shape ||
    !reportType.shape.ports ||
    reportType.shape.ports.length === 0
  ) {
    return [];
  }

  // Get ports directly from shapeDictionary.port since that's where the array is
  return shapeDictionary.port || [];
};

interface ShapeObjectWithOrder {
  shape_id: string;
  order: string[];
  shape: string;
}

const getMultiShapeWithOrder = (
  reportType: ReportType,
  shapeDictionary: GeoJsonDictionary | null,
): ShapeObjectWithOrder | null => {
  if (!shapeDictionary) {
    return null;
  }
  if (
    !reportType.shape ||
    !reportType.shape.multiShape ||
    reportType.shape.multiShape.length === 0
  ) {
    return null;
  }

  const shapeName = reportType.shape.multiShape[0].name;
  const featureCollection = shapeDictionary[shapeName];

  const order = featureCollection.features
    ? featureCollection.features.map((feature) => feature.properties.id)
    : [];

  const shapeObjectWithOrder = {
    shape_id: featureCollection.properties.id
      ? featureCollection.properties.id
      : '',
    order: order as string[],
    shape: JSON.stringify(featureCollection),
  };

  return shapeObjectWithOrder;
};

export const getReportParams = (
  reportType: ReportType,
  reportTemplateName: string,
  firstReportName: string,
  shapeDictionary: ShapeDictionaryType,
  startTime: Date,
  endTime: Date,
  miscellaneous: Record<string, any>,
  filter: ReportFilterType | null,
  reportTemplateId?: string,
  currentShapeType?: string,
  ships?: Ship[] | null,
  shipAttribute?: 'colorBy' | 'filterBy',
) => {
  // Set firstReportName to "N/A" if it's empty
  if (!firstReportName) {
    firstReportName = 'N/A';
  }

  if (reportType.type === ReportBackendTypes.AREA) {
    const shape = getSingleShape(
      reportType,
      shapeDictionary as GeoJsonDictionary,
      miscellaneous,
    );

    if (!shape) {
      return;
    }

    return getAreaReportParams(
      reportTemplateName,
      firstReportName,
      shape,
      startTime,
      endTime,
      miscellaneous,
      filter as Filter | string | null,
      reportTemplateId,
    );
  }

  if (reportType.type === ReportBackendTypes.MMSI_CHANGE) {
    const shape = getSingleShape(
      reportType,
      shapeDictionary as GeoJsonDictionary,
    );

    if (!shape) {
      return;
    }

    return getMmsiChangeReportParams(
      reportTemplateName,
      firstReportName,
      shape,
      startTime,
      endTime,
      miscellaneous,
      reportTemplateId,
    );
  }

  if (reportType.type === ReportBackendTypes.SHIPS) {
    return getShipsReportParams(
      reportTemplateName,
      firstReportName,
      startTime,
      endTime,
      filter as AutoCompleteShipInfo[],
      miscellaneous,
      reportTemplateId,
    );
  }

  if (reportType.type === ReportBackendTypes.TRACK) {
    const shape = getSingleShape(
      reportType,
      shapeDictionary as GeoJsonDictionary,
    );

    if (!shape) {
      return;
    }

    return getTrackReportParams(
      reportTemplateName,
      firstReportName,
      shape,
      startTime,
      endTime,
      filter as Filter | null,
      miscellaneous,
      reportTemplateId,
    );
  }

  if (reportType.type === ReportBackendTypes.DENSITY) {
    const shape = getSingleShape(
      reportType,
      shapeDictionary as GeoJsonDictionary,
    );

    if (!shape) {
      return;
    }

    return getDensityReportParams(
      reportTemplateName,
      firstReportName,
      shape,
      startTime,
      endTime,
      filter as Filter | null,
      miscellaneous,
      reportTemplateId,
    );
  }

  if (reportType.type === ReportBackendTypes.TRIPS) {
    if (!shapeDictionary) {
      return;
    }

    if (!reportType.shape) {
      return;
    }

    const departureShape = (shapeDictionary as GeoJsonDictionary)['departure'];
    const arrivalShape = (shapeDictionary as GeoJsonDictionary)['arrival'];
    const maxDuration = formatCustomDuration(miscellaneous.maxDuration);
    return getTripsReportParams(
      reportTemplateName,
      firstReportName,
      arrivalShape,
      departureShape,
      startTime,
      endTime,
      maxDuration,
      miscellaneous.excludeLocalOperations,
      filter as Filter | null,
      miscellaneous,
      reportTemplateId,
    );
  }

  if (reportType.type === ReportBackendTypes.NEAR_MISS_SHIP_TO_SHIP) {
    const shape = getSingleShape(
      reportType,
      shapeDictionary as GeoJsonDictionary,
    );

    if (!shape) {
      return;
    }

    return getNearMissShipToShipReportParams(
      reportTemplateName,
      firstReportName,
      shape,
      startTime,
      endTime,
      filter as Filter | AutoCompleteShipInfo[] | string | null,
      miscellaneous,
      reportTemplateId,
    );
  }

  if (reportType.type === ReportBackendTypes.NEAR_MISS_SHIP_TO_POINT) {
    const shape = getSingleShape(
      reportType,
      shapeDictionary as GeoJsonDictionary,
    );

    if (!shape) {
      return;
    }

    return getNearMissShipToPointReportParams(
      reportTemplateName,
      firstReportName,
      shape,
      startTime,
      endTime,
      filter as Filter | null,
      miscellaneous,
      reportTemplateId,
    );
  }

  if (reportType.type === ReportBackendTypes.PORT_CALLS) {
    let shape = null;
    if (currentShapeType === 'shape') {
      shape = getSingleShape(
        reportType,
        shapeDictionary as GeoJsonDictionary,
        miscellaneous,
      );

      if (!shape) {
        return;
      }
    }
    let portIds: string[] = [];
    if (currentShapeType === 'ports') {
      portIds = getPortIds(reportType, shapeDictionary as PortDictionary);
    }

    let worldShape = false;
    if (currentShapeType === 'worldShape') {
      worldShape = true;
    }

    return getPortCallsReportParams(
      reportTemplateName,
      firstReportName,
      shape,
      portIds,
      worldShape,
      startTime,
      endTime,
      filter as Filter | null,
      miscellaneous,
      reportTemplateId,
    );
  }

  if (reportType.type === ReportBackendTypes.PASSAGELINE) {
    const shape = getSingleShape(
      reportType,
      shapeDictionary as GeoJsonDictionary,
    );

    if (!shape) {
      return;
    }

    return getPassageLineReportParams(
      reportTemplateName,
      firstReportName,
      shape,
      startTime,
      endTime,
      filter as Filter | null,
      miscellaneous,
      reportTemplateId,
    );
  }

  if (reportType.type === ReportBackendTypes.PASSAGELINE_SHIPS) {
    const shape = getMultiShapeWithOrder(
      reportType,
      shapeDictionary as GeoJsonDictionary,
    );

    if (!shape) {
      return;
    }

    return getPassageLineShipsReportParams(
      reportTemplateName,
      firstReportName,
      shape,
      startTime,
      endTime,
      filter as Filter | null,
      miscellaneous,
      reportTemplateId,
    );
  }

  if (reportType.type === ReportBackendTypes.TRANSPONDER_OFF) {
    const shape = getSingleShape(
      reportType,
      shapeDictionary as GeoJsonDictionary,
    );

    if (!shape) {
      return;
    }

    return getTransponderOffReportParams(
      reportTemplateName,
      firstReportName,
      shape,
      startTime,
      endTime,
      filter as Filter | null,
      miscellaneous,
      reportTemplateId,
    );
  }
  if (reportType.type === ReportBackendTypes.SOG_DISTRIBUTION) {
    const shape = getSingleShape(
      reportType,
      shapeDictionary as GeoJsonDictionary,
    );

    if (!shape) {
      return;
    }

    return getSpeedOverGroundReportParams(
      reportTemplateName,
      firstReportName,
      shape,
      startTime,
      endTime,
      filter as AutoCompleteShipInfo[] | null,
      miscellaneous,
      reportTemplateId,
    );
  }
  if (reportType.type === ReportBackendTypes.FISHING) {
    const shape = getSingleShape(
      reportType,
      shapeDictionary as GeoJsonDictionary,
    );

    if (!shape) {
      return;
    }

    return getFishingReportParams(
      reportTemplateName,
      firstReportName,
      shape,
      startTime,
      endTime,
      filter as Filter | null,
      miscellaneous,
      reportTemplateId,
    );
  }

  if (reportType.type === ReportBackendTypes.MULTI_SHAPE) {
    const shape = getMultiShapeWithOrder(
      reportType,
      shapeDictionary as GeoJsonDictionary,
    );

    if (!shape) {
      return;
    }

    return getMultiShapeReportParams(
      reportTemplateName,
      firstReportName,
      shape,
      startTime,
      endTime,
      filter as Filter | null,
      miscellaneous,
      reportTemplateId,
    );
  }

  if (reportType.type === ReportBackendTypes.TRANSIT_PILOTAGE) {
    const shape = getMultiShapeWithOrder(
      reportType,
      shapeDictionary as GeoJsonDictionary,
    );

    if (!shape) {
      return;
    }

    const maxDuration = formatCustomDuration(miscellaneous.maxDuration);

    let shiplist = '';
    if (typeof filter === 'object' && filter !== null && 'shiplist' in filter) {
      shiplist = filter.shiplist as string;
    }

    let filterFromObject = null;
    if (typeof filter === 'object' && filter !== null && 'filter' in filter) {
      filterFromObject = filter.filter as Filter | null;
    }

    return getTransitPilotageReportParams(
      reportTemplateName,
      firstReportName,
      shape,
      startTime,
      endTime,
      maxDuration,
      shiplist,
      filterFromObject,
      miscellaneous,
      reportTemplateId,
    );
  }

  if (reportType.type === ReportBackendTypes.PILOT_OPERATIONS) {
    const shape = getSingleShape(
      reportType,
      shapeDictionary as GeoJsonDictionary,
    );

    if (!shape) {
      return;
    }

    return getPilotReportParams(
      reportTemplateName,
      firstReportName,
      shape,
      startTime,
      endTime,
      filter as Filter | null,
      miscellaneous,
      reportTemplateId,
    );
  }

  if (reportType.type === ReportBackendTypes.COVERAGE) {
    const shape = getSingleShape(
      reportType,
      shapeDictionary as GeoJsonDictionary,
    );

    if (!shape) {
      return;
    }

    return getCoverageReportParams(
      reportTemplateName,
      firstReportName,
      shape,
      startTime,
      endTime,
      filter as Filter | null,
      miscellaneous,
      reportTemplateId,
    );
  }

  if (reportType.type === ReportBackendTypes.CSV_DATA_EXPORT) {
    const shape = getSingleShape(
      reportType,
      shapeDictionary as GeoJsonDictionary,
    );

    if (!shape) {
      return;
    }

    return getCsvExportReportParams(
      reportTemplateName,
      firstReportName,
      shape,
      startTime,
      endTime,
      filter as Filter | null,
      miscellaneous,
      reportTemplateId,
    );
  }

  if (reportType.type === ReportBackendTypes.CSV_DATA_EXPORT_FAST) {
    const shape = getSingleShape(
      reportType,
      shapeDictionary as GeoJsonDictionary,
    );

    if (!shape) {
      return;
    }

    return getCsvExportFastReportParams(
      reportTemplateName,
      firstReportName,
      shape,
      startTime,
      endTime,
      filter as Filter | null,
      miscellaneous,
      reportTemplateId,
    );
  }

  if (reportType.type === ReportBackendTypes.FERRY_ROUTE) {
    return getFerryRouteReportParams(
      reportTemplateName,
      firstReportName,
      startTime,
      endTime,
      filter as string | null,
      miscellaneous,
      reportTemplateId,
    );
  }

  if (reportType.type === ReportBackendTypes.WATCHDOG_EVENTS) {
    const shape = getSingleShape(
      reportType,
      shapeDictionary as GeoJsonDictionary,
    );

    if (!shape) {
      return;
    }

    return getWatchdogReportParams(
      reportTemplateName,
      firstReportName,
      shape,
      startTime,
      endTime,
      miscellaneous,
      reportTemplateId,
    );
  }

  if (reportType.type === ReportBackendTypes.REPLAY_VIDEO) {
    const shape = getSingleShape(
      reportType,
      shapeDictionary as GeoJsonDictionary,
    );

    if (!shape) {
      return;
    }

    return getReplayVideoReportParams(
      reportTemplateName,
      firstReportName,
      shape,
      startTime,
      endTime,
      miscellaneous,
      reportTemplateId,
      ships,
      shipAttribute,
    );
  }

  if (reportType.type === ReportBackendTypes.SQL) {
    const shape = getSingleShape(
      reportType,
      shapeDictionary as GeoJsonDictionary,
    );

    if (!shape) {
      return;
    }

    return getSqlReportParams(
      reportTemplateName,
      firstReportName,
      shape,
      startTime,
      endTime,
      miscellaneous,
      reportTemplateId,
    );
  }

  if (reportType.type === ReportBackendTypes.ATON_DETAILED) {
    return getAtonDetailedReportParams(
      reportTemplateName,
      firstReportName,
      startTime,
      endTime,
      miscellaneous,
      reportTemplateId,
    );
  }

  if (reportType.type === ReportBackendTypes.ATON_OVERVIEW) {
    return getAtonOverviewReportParams(
      reportTemplateName,
      firstReportName,
      startTime,
      endTime,
      miscellaneous,
      reportTemplateId,
    );
  }
};
